import { View } from "ui/atoms/view";
import { FormControlledResidentMultiSelect } from "ui/molecules/form/FormControlledResidentMultiSelect";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import { useGenerateReport } from "../hooks/useGenerateReport";
import KeyValueSelector from "ui/organisms/KeyValueSelector";
import { Controller, useWatch } from "react-hook-form";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { Button, ButtonText } from "ui/gluestack/button";
import { requiredValidation } from "ui/utils/Validators";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";
import FormControlledLocationSelector from "app/components/molecules/FormControlledLocationSelector";

interface Props {
  initialResident?: ResidentModel;
  onClose: () => void;
  onSuccess?: (newIds: number[]) => void;
}

export default function GenerateReportFormTemplate({
  initialResident,
  onClose,
  onSuccess,
}: Props) {
  const { control, reportCategories, isLoading, submit, isSubmitting } =
    useGenerateReport({
      initialResident,
      onSuccess: (newIds) => {
        onClose();
        onSuccess?.(newIds);
      },
    });

  const reportCategoryOptions =
    reportCategories?.map((category) => ({
      label: category.title,
      value: category.key,
    })) ?? [];

  const locationSelected = useWatch({
    control,
    name: "location_id",
  });

  if (isLoading) {
    return (
      <View className="flex-1 justify-center items-center my-10">
        <LoadingSpinner />
      </View>
    );
  }

  return (
    <View>
      <View className="border border-gray-200 w-full mt-6 mb-10" />
      <FormControlledLocationSelector
        control={control}
        name="location_id"
        className="mb-9"
        label="Location"
      />
      <FormControlledResidentMultiSelect
        label="Residents"
        name="resident_ids"
        control={control}
        locationId={locationSelected}
        isRequired
        preselectedResidents={initialResident ? [initialResident] : []}
      />
      <View className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-9">
        <FormControlledDatePicker
          id="start_date"
          name="start_date"
          control={control}
          label="Start date"
          placeholder={"00/00/0000"}
          isRequired
        />
        <FormControlledDatePicker
          id="end_date"
          name="end_date"
          control={control}
          label="End date"
          placeholder={"00/00/0000"}
          isRequired
        />
      </View>
      <Controller
        control={control}
        name="categories"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <KeyValueSelector<string>
            title="Report Items"
            subtitle="Choose the items to include in your report"
            options={reportCategoryOptions}
            isMultiSelect={true}
            selectedValues={value}
            onChange={onChange}
            errorMessage={error}
          />
        )}
        rules={{ required: requiredValidation("Report Items") }}
      />
      <View className="col-span-full">
        <View className="flex justify-end gap-4 sm:flex-row sm:gap-x-4 mt-10">
          <Button
            action="primary"
            size="sm"
            variant="outline"
            onPress={onClose}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            action="primary"
            size="sm"
            onPress={submit}
            disabled={isSubmitting}
          >
            <ButtonText className="text-white">Generate</ButtonText>
          </Button>
        </View>
      </View>
    </View>
  );
}
